import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EbdaEduNavbar from "components/navbars/EbdaEduNavbar";
import { useLanguage } from "context/LanguageContext";
import { useTheme } from "context/ThemeContext";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { uploadUserImage } from "services/admins";
import {
  fetchWisdomRelatedEmployees,
  fetchWisdomRelatedSchools,
  fetchWisdomRelatedStudents,
} from "services/wisdom/wisdomData";

const fullName = (p) =>
  [p?.first_name, p?.middle_name, p?.last_name].filter(Boolean).join(" ");

const SearchableSelect = ({ options, value, onChange, placeholder, searchPlaceholder, noResultsText }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => String(o.value) === String(value));

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(term) || String(o.value).includes(term)
    );
  }, [options, search]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none flex items-center justify-between gap-2 text-start"
      >
        <span className={`truncate ${selected ? "" : "text-slate-400"}`}>
          {selected ? `${selected.label} (#${selected.value})` : placeholder}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className="text-xs text-slate-500" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <input
            autoFocus
            type="text"
            className="w-full px-3 py-2 border-b border-slate-200 outline-none rounded-t-xl"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-500">{noResultsText}</li>
            ) : (
              filteredOptions.map((o) => (
                <li
                  key={o.value}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${String(o.value) === String(value) ? "bg-blue-100 font-semibold" : ""
                    }`}
                >
                  {o.label} <span className="text-slate-400">#{o.value}</span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const WabysUserImageUpload = () => {
  const { language } = useLanguage();
  const { darkMode } = useTheme();

  const [schools, setSchools] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [students, setStudents] = useState([]);

  const [orgId, setOrgId] = useState("");
  const [userType, setUserType] = useState(""); // "employee" | "student"
  const [userId, setUserId] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageFileName, setImageFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [schoolsRes, employeesRes, studentsRes] = await Promise.all([
          fetchWisdomRelatedSchools(),
          fetchWisdomRelatedEmployees(),
          fetchWisdomRelatedStudents(),
        ]);
        setSchools(schoolsRes);
        setEmployees(employeesRes);
        setStudents(studentsRes);
      } catch (err) {
        toast.error(language ? "Failed to load data" : "فشل تحميل البيانات");
      }
    };

    loadAll();
  }, []);

  const userOptions = useMemo(() => {
    console.log(students)
    if (userType === "employee") {
      return employees
        .filter((emp) => emp?.employee?.id && (!orgId || emp.employee.organization_id === Number(orgId)))
        .map((emp) => ({ value: emp.id, label: fullName(emp.employee) }));
    }
    if (userType === "student") {
      return students
        .filter((std) => std?.user_id && (!orgId || std.school_id === Number(orgId)))
        .map((std) => ({ value: std.user_id, label: fullName(std) }));
    }
    return [];
  }, [userType, orgId, employees, students]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageFileName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId || !imageFile) {
      toast.error(language ? "Fill all fields" : "املأ كل الحقول");
      return;
    }

    const formData = new FormData();
    formData.append("user_id", Number(userId));
    formData.append("file", imageFile);

    setSubmitting(true);
    try {
      await uploadUserImage(formData);

      setUserId("");
      setImageFile(null);
      setImageFileName("");

      toast.success(language ? "Image uploaded" : "تم رفع الصورة");
    } catch (err) {
      toast.error(language ? "Failed to upload image" : "فشل رفع الصورة");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`w-full min-h-screen flex flex-col items-center ${darkMode && "bg-watomsBlue text-white"}`}>
      <EbdaEduNavbar />

      <div className="w-[90%] max-w-md mt-8" dir={language ? "ltr" : "rtl"}>
        <div
          className={`rounded-2xl border shadow-sm p-4 ${darkMode ? "bg-white text-black border-black/10" : "bg-white border-slate-200"
            }`}
        >
          <h2 className="font-bold text-lg mb-4">
            {language ? "Upload User Image" : "رفع صورة المستخدم"}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-3">
            <select
              className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setUserId("");
              }}
            >
              <option value="">{language ? "All Organizations" : "كل المؤسسات"}</option>
              {schools?.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>

            <select
              className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
                setUserId("");
              }}
            >
              <option value="">{language ? "Select Type" : "اختر النوع"}</option>
              <option value="employee">{language ? "Employee" : "موظف"}</option>
              <option value="student">{language ? "Student" : "طالب"}</option>
            </select>

            {userType && (
              <SearchableSelect
                options={userOptions}
                value={userId}
                onChange={setUserId}
                placeholder={
                  userType === "employee"
                    ? (language ? "Select Employee" : "اختر الموظف")
                    : (language ? "Select Student" : "اختر الطالب")
                }
                searchPlaceholder={language ? "Search by name or ID..." : "ابحث بالاسم أو المعرّف..."}
                noResultsText={language ? "No results" : "لا توجد نتائج"}
              />
            )}

            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-blue-100 file:text-blue-700"
              />
              {imageFileName && (
                <p className="text-xs text-slate-600">
                  {language ? "File" : "الملف"}: {imageFileName}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl py-2 font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting
                ? (language ? "Uploading..." : "جاري الرفع...")
                : (language ? "Upload Image" : "رفع الصورة")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WabysUserImageUpload;
